import { useState, useRef, useEffect } from "react";
import { Search, X } from "lucide-react";
import { format, parseISO, isValid } from "date-fns";
import { apiGet } from "@/lib/apiClient";
import type { MeetingNameSearchResponse } from "@/types/draftCalendar";

interface CalendarEventSearchProps {
  userEmail: string;
  /** Current value displayed in the input */
  value: string;
  onValueChange: (value: string) => void;
  /** Called when the user selects an item from the dropdown */
  onSelect: (item: MeetingNameSearchResponse | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Mirrors Blazor CalendarEventSearch — autocomplete that searches
 * DraftCalendar/search and shows meeting name + date range in the dropdown.
 * Debounce: 1000 ms, min length: 3 chars.
 */
export function CalendarEventSearch({
  userEmail,
  value,
  onValueChange,
  onSelect,
  placeholder = "Search",
  disabled = false
}: CalendarEventSearchProps) {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<MeetingNameSearchResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    onValueChange(val);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!val.trim() || val.trim().length < 3) {
      setResults([]);
      setOpen(false);
      if (!val.trim()) onSelect(null);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await apiGet<MeetingNameSearchResponse[]>(
          `DraftCalendar/search?searchParam=${encodeURIComponent(val.trim())}&userEmail=${encodeURIComponent(userEmail)}`
        );
        setResults(Array.isArray(data) ? data : []);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 1000);
  }

  function handleClear() {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onValueChange("");
    setResults([]);
    setOpen(false);
    onSelect(null);
  }

  function handleSelect(item: MeetingNameSearchResponse) {
    onValueChange(item.meetingName ?? "");
    setOpen(false);
    onSelect(item);
  }

  function formatDateRange(item: MeetingNameSearchResponse) {
    try {
      const start = item.eventStart ? parseISO(item.eventStart) : null;
      const end = item.eventEnd ? parseISO(item.eventEnd) : null;
      if (start && isValid(start) && end && isValid(end)) {
        return `${format(start, "MMM dd")} - ${format(end, "MMM dd, yyyy")}`;
      }
      if (start && isValid(start)) return format(start, "MMM dd, yyyy");
    } catch { /* ignore */ }
    return "";
  }

  return (
    <div ref={containerRef} className="calendar-event-search-wrapper">
      <div className="calendar-event-search-input-wrap">
        <Search className="calendar-event-search-icon" aria-hidden="true" />
        <input
          type="text"
          className="calendar-event-search-input custom-autocomplete-search"
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          onChange={handleChange}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          aria-label="Search meetings"
          aria-autocomplete="list"
          aria-expanded={open}
        />
        {value && (
          <button
            type="button"
            className="calendar-event-search-clear"
            onClick={handleClear}
            aria-label="Clear search"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {open && (
        <div className="calendar-event-search-dropdown User-popup-container" role="listbox">
          {loading ? (
            <div className="calendar-event-search-loading">Searching…</div>
          ) : results.length === 0 ? (
            <div className="calendar-event-search-empty">No results found</div>
          ) : (
            results.map((item) => (
              <div
                key={item.id}
                role="option"
                aria-selected={false}
                className="autocomplete-event-row"
                onMouseDown={(e) => { e.preventDefault(); handleSelect(item); }}
              >
                <div className="calendar-event-search-row-inner">
                  <span className="event-name-line">{item.meetingName}</span>
                  <span className="event-date-line">{formatDateRange(item)}</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
